// ADR 17.10.26i §1 — the tours, as data: native's `OnboardingFlowCoordinator`
// groups, one per screen, each on the route the web draws that screen at.
//
// Pure data, no React and no wasm: `tourSession.ts`, `coachGate.ts` and the
// boot-graph `HeaderSwitcher` all import this file (§5, §8). Only
// `language_change` is listed; `lesson_vocab_save` waits for the `+` the web's
// lesson detail does not draw yet (§7).

import type { CoachStep } from '../coachMachine'
import type { CoachScreen } from '../screens'
import { COACH_ANCHORS } from '../tips/catalogue'

/** The ids a tour step points at that no screen tip already registers (§5). */
export const TOUR_ANCHORS = {
  headerSettings: 'header.settings',
  languageSource: 'language.source',
  languageTarget: 'language.target',
} as const

/** A group that has not presented by now is cancelled: a slow route spends nothing (§1). */
export const ARRIVAL_TIMEOUT_MS = 4000
/** A group with some, not all, of its targets drawn presents this long after the first resolved. */
export const SETTLE_MS = 300

export type FlowId = 'language_change'

export interface TourGroup {
  /** The slug its events carry and its route's own tips are held under. */
  screen: CoachScreen
  /** The pathname the group is drawn at (`matchPath` pattern). */
  route: string
  steps: readonly CoachStep[]
  /** Every group but the last hands over: its last step's button reads `labelKey` and leads to `to`. */
  action?: { labelKey: string; to: string }
}

function step(anchor: string, stem: string): CoachStep {
  return { id: anchor, anchor, titleKey: `coach_flow_${stem}_title`, bodyKey: `coach_flow_${stem}_desc` }
}

export const TOUR_FLOWS: Record<FlowId, readonly TourGroup[]> = {
  language_change: [
    {
      screen: 'home',
      route: '/',
      steps: [step(TOUR_ANCHORS.headerSettings, 'open_settings')],
      action: { labelKey: 'coach_flow_open_settings_action', to: '/account' },
    },
    {
      screen: 'settings',
      route: '/account',
      steps: [step(COACH_ANCHORS.settingsLanguage, 'open_language')],
      action: { labelKey: 'coach_flow_open_language_action', to: '/account/language' },
    },
    {
      screen: 'language_settings',
      route: '/account/language',
      steps: [step(TOUR_ANCHORS.languageSource, 'pick_source'), step(TOUR_ANCHORS.languageTarget, 'pick_target')],
    },
  ],
}

export const FLOW_IDS = Object.keys(TOUR_FLOWS) as FlowId[]

export const groupOf = (flowId: FlowId, group: number): TourGroup | undefined => TOUR_FLOWS[flowId][group]
